import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Sparkles, CreditCard, Gift, Share2, Download } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import { QRCodeCanvas } from 'qrcode.react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SVCSo382aFGgdDTM8fb8m063DuKfpCwaW8SSpAZ3KBkKMkGeEVhUkMUeA22P7hQwe04Bxs5eqRx0NhhAjdS87Qm001YK7K9gw');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#E6D7C3',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#8A7355',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

function PaymentForm({ spa, treatment, onSuccess, recipientInfo, message }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { data } = await axios.post(`${API}/spa/purchase`, {
        spa_id: spa.id,
        treatment_id: treatment.id,
        recipient_info: recipientInfo,
        personal_message: message
      });

      const result = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else {
        const confirmRes = await axios.post(`${API}/spa/confirm-payment/${data.transaction_id}`);
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
      <div style={{
        background: 'rgba(230, 215, 195, 0.05)',
        border: '1px solid #A57C33',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {error && (
        <div style={{
          padding: '12px',
          background: 'rgba(220, 38, 38, 0.1)',
          border: '1px solid rgba(220, 38, 38, 0.3)',
          borderRadius: '8px',
          color: '#fca5a5',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        style={{
          width: '100%',
          padding: '16px',
          background: processing ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
          color: '#2C1B13',
          border: 'none',
          borderRadius: '12px',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: processing ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.3s ease'
        }}
      >
        <CreditCard size={20} />
        {processing ? 'Processing...' : `Pay $${treatment.price.toFixed(2)}`}
      </button>
    </form>
  );
}

const SendSpaPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: spas, 2: treatments, 3: details, 4: payment, 5: success
  const [spas, setSpas] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [selectedSpa, setSelectedSpa] = useState(null);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState('');
  const [message, setMessage] = useState('');
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSpas();
  }, []);

  const fetchSpas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/spa/venues`);
      setSpas(response.data);
    } catch (error) {
      console.error('Error fetching spas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTreatments = async (spaId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/spa/${spaId}/treatments`);
      setTreatments(response.data);
      setStep(2);
    } catch (error) {
      console.error('Error fetching treatments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSpaSelect = (spa) => {
    setSelectedSpa(spa);
    fetchTreatments(spa.id);
  };

  const handleTreatmentSelect = (treatment) => {
    setSelectedTreatment(treatment);
    setStep(3);
  };

  const handlePaymentSuccess = (data) => {
    setQrData(data);
    setStep(5);
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `spa-qr-${qrData.unique_code}.png`;
    link.href = url;
    link.click();
  };

  const shareQRCode = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${qrData.treatment_name} at ${qrData.spa_name}`,
          text: `I sent you a spa treatment! ${message ? message : 'Enjoy!'}`,
          url: qrData.qr_url
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(qrData.qr_url);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div style={{ paddingBottom: '80px', minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)' }}>
      <div className="page-header">
        <button
          onClick={() => step === 1 ? navigate('/gifts') : step === 2 ? setStep(1) : step === 3 ? setStep(2) : step === 4 ? setStep(3) : setStep(1)}
          style={{ background: 'none', border: 'none', color: '#E6D7C3', cursor: 'pointer', padding: '8px' }}
        >
          <ArrowLeft size={24} />
        </button>
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>
          {step === 5 ? 'SPA GIFT SENT!' : 'GIFT SPA TREATMENT'}
        </h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Progress */}
        {step < 5 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', justifyContent: 'center' }}>
            {[1, 2, 3, 4].map(s => (
              <div
                key={s}
                style={{
                  width: '40px',
                  height: '4px',
                  background: s <= step ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.2)',
                  borderRadius: '2px'
                }}
              />
            ))}
          </div>
        )}

        {/* Step 1: Select Spa */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
              Select Spa
            </h2>
            {loading ? (
              <p style={{ color: '#A57C33' }}>Loading spas...</p>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {spas.map(spa => (
                  <div
                    key={spa.id}
                    onClick={() => handleSpaSelect(spa)}
                    style={{
                      background: 'rgba(230, 215, 195, 0.05)',
                      border: '1px solid #A57C33',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      gap: '16px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.05)'}
                  >
                    {spa.photo && (
                      <img
                        src={spa.photo}
                        alt={spa.name}
                        style={{ width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '18px', color: '#E6D7C3', marginBottom: '4px', fontWeight: 'bold' }}>
                        {spa.name}
                      </h3>
                      <p style={{ fontSize: '14px', color: '#A57C33', marginBottom: '4px' }}>
                        <MapPin size={14} style={{ display: 'inline', marginRight: '4px' }} />
                        {spa.address}
                      </p>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        background: 'rgba(184, 164, 201, 0.2)',
                        color: '#B8A4C9',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        Luxury Spa
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Treatment */}
        {step === 2 && selectedSpa && (
          <div>
            <div style={{
              background: 'rgba(230, 215, 195, 0.05)',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '4px' }}>Selected Spa</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold' }}>{selectedSpa.name}</h3>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
              Select Treatment
            </h2>
            {loading ? (
              <p style={{ color: '#A57C33' }}>Loading treatments...</p>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {treatments.map(treatment => (
                  <div
                    key={treatment.id}
                    onClick={() => handleTreatmentSelect(treatment)}
                    style={{
                      background: 'rgba(230, 215, 195, 0.05)',
                      border: '1px solid #A57C33',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.05)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '18px', color: '#E6D7C3', marginBottom: '4px', fontWeight: 'bold' }}>
                          {treatment.name}
                        </h3>
                        <p style={{ fontSize: '14px', color: '#8A7355', marginBottom: '6px' }}>
                          {treatment.duration} minutes
                        </p>
                        <p style={{ fontSize: '14px', color: '#A57C33' }}>
                          {treatment.description}
                        </p>
                      </div>
                      <span style={{
                        fontSize: '18px',
                        color: '#D7B56D',
                        fontWeight: 'bold',
                        marginLeft: '16px'
                      }}>
                        ${treatment.price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && selectedTreatment && (
          <div>
            <div style={{
              background: 'rgba(230, 215, 195, 0.05)',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#A57C33', fontSize: '13px' }}>{selectedSpa.name}</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold' }}>{selectedTreatment.name}</h3>
              <p style={{ color: '#D7B56D', fontSize: '16px', fontWeight: 'bold' }}>${selectedTreatment.price.toFixed(2)}</p>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
              Add Details
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>
                Recipient Info (Optional)
              </label>
              <input
                type="text"
                placeholder="Name, email, or phone"
                value={recipientInfo}
                onChange={(e) => setRecipientInfo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(230, 215, 195, 0.05)',
                  border: '1px solid #A57C33',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>
                Personal Message (Optional)
              </label>
              <textarea
                placeholder="Add a personal message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows="4"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(230, 215, 195, 0.05)',
                  border: '1px solid #A57C33',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '16px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <button
              onClick={() => setStep(4)}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                color: '#2C1B13',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Gift size={20} />
              Continue to Payment
            </button>
          </div>
        )}

        {/* Step 4: Payment */}
        {step === 4 && selectedTreatment && (
          <div>
            <div style={{
              background: 'rgba(230, 215, 195, 0.05)',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#E6D7C3', fontSize: '16px', marginBottom: '12px' }}>Order Summary</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#A57C33' }}>{selectedTreatment.name}</span>
                <span style={{ color: '#E6D7C3' }}>${selectedTreatment.price.toFixed(2)}</span>
              </div>
              <div style={{ borderTop: '1px solid #A57C33', marginTop: '12px', paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold' }}>Total</span>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '18px' }}>${selectedTreatment.price.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
              Payment Details
            </h2>

            <Elements stripe={stripePromise}>
              <PaymentForm
                spa={selectedSpa}
                treatment={selectedTreatment}
                onSuccess={handlePaymentSuccess}
                recipientInfo={recipientInfo}
                message={message}
              />
            </Elements>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && qrData && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #B8A4C9 0%, #9B7FAD 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Sparkles size={40} color="#2C1B13" />
            </div>

            <h2 style={{
              fontSize: '28px',
              color: '#E6D7C3',
              marginBottom: '12px',
              fontFamily: 'Playfair Display, serif'
            }}>
              Spa Gift Sent!
            </h2>

            <p style={{ color: '#A57C33', marginBottom: '32px', fontSize: '16px' }}>
              Share this QR code with the recipient
            </p>

            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '16px',
              display: 'inline-block',
              marginBottom: '24px'
            }}>
              <QRCodeCanvas
                id="qr-code-canvas"
                value={qrData.qr_url}
                size={250}
                level="H"
              />
            </div>

            <div style={{
              background: 'rgba(230, 215, 195, 0.05)',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '8px' }}>Treatment Details</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                {qrData.treatment_name}
              </h3>
              <p style={{ color: '#D7B56D' }}>{qrData.spa_name}</p>
              <p style={{ color: '#8A7355', fontSize: '14px', marginTop: '8px' }}>
                Code: {qrData.unique_code}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <button
                onClick={shareQRCode}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                  color: '#2C1B13',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Share2 size={20} />
                Share
              </button>
              <button
                onClick={downloadQRCode}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'rgba(230, 215, 195, 0.1)',
                  color: '#E6D7C3',
                  border: '1px solid #A57C33',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Download size={20} />
                Download
              </button>
            </div>

            <button
              onClick={() => navigate('/gifts')}
              style={{
                width: '100%',
                padding: '14px',
                background: 'none',
                color: '#A57C33',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Back to Gifts
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendSpaPage;
